import { CHANNEL_NAMES, formatDate } from "./marketOverviewText";
import { BUSINESS_TYPE_LABELS } from "./businessTypes/labels";

// The words used for an advertiserOverview() result, so the page and (later)
// the MCP server describe it the same way. Only these claims are ever made:
// appeared in X of Y searches (Y always says what it counts) · average position ·
// seen most often · highest average position · shown with a price ·
// first seen on [date] · no ads seen in X of Y.
// Comparisons are two observed facts side by side - never "losing", "behind",
// "should" or "missed". Business type is always "classified as".
// "Searches", never "scans". Status words (so far, stopped early, estimated)
// come from marketOverviewText.js: soFar(), STATUS_LABELS, coverageNote().

export { soFar, coverageNote, providerError, STATUS_LABELS } from "./marketOverviewText";

export const PINNED_ALWAYS_SHOWN = "Your pinned advertiser is always shown";
export const PICKER_QUESTION = "Which advertiser is this snapshot for?";
export const YOUR_BUSINESS = "Your business";
export const TOP3_LABEL = "Top 3 others (average)";

// Channels by their one name: "Appeared in Google Ads in 4 of 23 searches",
// "Average Google Maps position 2.5".
const CHANNEL_PLACES = { ppc: CHANNEL_NAMES.ppc, local: CHANNEL_NAMES.local, seo: CHANNEL_NAMES.seo, shopping: CHANNEL_NAMES.shopping, lsa: CHANNEL_NAMES.lsa };
const POSITION_NAMES = CHANNEL_PLACES;

function plural(n, word, many = `${word}s`) {
  return `${n} ${n === 1 ? word : many}`;
}

// 1 -> "1st", 2 -> "2nd", 11 -> "11th", 23 -> "23rd"
export function ordinal(n) {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${{ 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th"}`;
}

// "37 of 118 searches", or "Not seen in any of 118 searches".
export function searchesText(searches, of) {
  return searches > 0 ? `${searches} of ${plural(of, "search", "searches")}` : `Not seen in any of ${plural(of, "search", "searches")}`;
}

// "Ranked 7th of 34 advertisers" ("" when not seen: it isn't ranked).
export function rankText(result) {
  const { rank } = result;
  if (!rank || rank.position == null) return "";
  return `Ranked ${ordinal(rank.position)} of ${plural(rank.of, "advertiser")}`;
}

// "Classified as Local business"
export function classifiedAs(businessType) {
  return `Classified as ${BUSINESS_TYPE_LABELS[businessType.type] || businessType.type}`;
}

// "#1" for the leader, "Next" when the pinned advertiser is #1 itself.
export function leaderLabel(leader) {
  return leader && leader.role === "next" ? "Next" : "#1";
}

// "You: 37 of 118 searches · #1 smartflowservices.co.uk: 86 of 118"
export function comparisonLine(result) {
  const { rank, comparison } = result;
  if (!rank) return "";
  const you = `You: ${searchesText(rank.visibility.searches, rank.visibility.of)}`;
  const leader = comparison.leader;
  if (!leader) return you;
  return `${you} · ${leaderLabel(leader)} ${leader.displayName}: ${leader.visibility.searches} of ${leader.visibility.of}`;
}

// "34.7 of 118" - an average of counts, always with "of".
export function averageOfText(value, of) {
  return value == null ? "–" : `${value} of ${of}`;
}

// The channel card's heading line: "ads ran in 96 of 118" / "shown in 112 of 118".
export function channelShownText(channel, block, of) {
  const n = block ? block.searchesWithChannel : 0;
  return channel === "ppc" ? `ads ran in ${n} of ${of}` : `shown in ${n} of ${of}`;
}

// "avg pos 2.7 · best 1" (short, to fit a channel tile)
export function positionText(you) {
  if (!you || you.avgPosition == null) return "";
  return `avg pos ${you.avgPosition}${you.bestPosition != null ? ` · best ${you.bestPosition}` : ""}`;
}

// A "Where you show up" cell: "5 of 6" (the colour and legend say seen /
// not seen where ads ran / not seen, no ads - Scott, 14 Oct 2026).
export function cellText(value, searches) {
  if (searches === 0) return "–";
  return `${value} of ${searches}`;
}

// The cell's hover text: "S1 · plumber: seen in 5 of 6 searches · ads ran in 6".
export const CELL_STATUS_WORDS = { seen: "seen", not_seen_ads_ran: "not seen · ads ran", not_seen_no_ads: "not seen · no ads" };

// The line above the grid, from gridSummary (or a channel's table summary,
// which has no postcode counts).
// "Seen in 9 of 12 keyword × postcode · not seen in 2 where ads ran · seen in 3 of 5 postcodes"
export function gridSummaryText(summary) {
  if (!summary || summary.cells === 0) return "No completed searches for these filters.";
  const parts = [
    `Seen in ${summary.cellsSeen} of ${summary.cells} keyword × postcode`,
    `not seen in ${summary.cellsNotSeenAdsRan} where ads ran`,
  ];
  if (summary.postcodes != null) parts.push(`seen in ${summary.postcodesSeen} of ${plural(summary.postcodes, "postcode")}`);
  return parts.join(" · ");
}

// What an "of" counts, for appeared_in facts.
function scopeOf(fact) {
  if (fact.scope === "ads_ran") return `of the ${plural(fact.of, "search", "searches")} where ads ran`;
  if (fact.scope === "postcodes") return `of ${plural(fact.of, "postcode")}`;
  return `of ${plural(fact.of, "search", "searches")}`;
}

// One "Observed" chip: { label, value } - the page shows the value in bold.
export function factParts(fact) {
  switch (fact.claim) {
    case "appeared_in":
      if (fact.scope === "any" && fact.value === 0) return { label: "Not seen", value: `in any of ${plural(fact.of, "search", "searches")}` };
      if (fact.scope === "postcodes") return { label: "Seen in", value: `${fact.value} ${scopeOf(fact)}` };
      if (CHANNEL_PLACES[fact.scope]) return { label: `Appeared in ${CHANNEL_PLACES[fact.scope]} in`, value: `${fact.value} ${scopeOf(fact)}` };
      return { label: "Appeared in", value: `${fact.value} ${scopeOf(fact)}` };
    case "average_position":
      return { label: `Average ${POSITION_NAMES[fact.channel]} position`, value: `${fact.value}` };
    case "seen_most_often":
      return {
        label: CHANNEL_PLACES[fact.scope] ? `Seen most often in ${CHANNEL_PLACES[fact.scope]}` : "Seen most often",
        value: `${fact.value} of ${plural(fact.of, "search", "searches")}`,
      };
    case "highest_average_position":
      return { label: `Highest average ${POSITION_NAMES[fact.channel]} position`, value: `${fact.value}` };
    case "shown_with_a_price":
      return { label: "Shown with a price", value: `${fact.value} of ${plural(fact.of, "search", "searches")}` };
    case "first_seen":
      return { label: "First seen", value: `on ${formatDate(fact.at)}` };
    default:
      return { label: "", value: "" };
  }
}

export function factText(fact) {
  const { label, value } = factParts(fact);
  return `${label} ${value}`.trim();
}

export { CHANNEL_NAMES };
